import posthog from 'posthog-js';
import { UserClaims } from '../model/userClaims';
import { sanitizePostHogEvent } from './posthogSanitize';

export type InitializePostHogConfig = {
    key: string | undefined | null;
    host: string | undefined | null;
    claims?: UserClaims | null;
    buildNumber?: string;
    enterprise?: {
        enterpriseId?: string | null;
        enterpriseName?: string | null;
    } | null;
};

/**
 * Claims used by the `before_send` sanitizer. `posthog.init()` — and the `before_send`
 * closure it creates — only ever runs on the first successful `initializePostHog` call,
 * so the closure reads this ref instead of freezing whatever was passed that first time.
 * Every `initializePostHog` call updates it, including the `__loaded` early-return path.
 */
let currentClaims: UserClaims | null | undefined = null;

/**
 * Registers the enterprise cohort group. Safe to call whenever enterprise becomes available.
 * Never identifies a person — cohorts are environment + enterprise only.
 */
export function registerEnterpriseGroup(
    enterprise: { enterpriseId?: string | null; enterpriseName?: string | null } | null | undefined
): void {
    const enterpriseId = enterprise?.enterpriseId?.trim();
    if (!enterpriseId) {
        return;
    }
    if (!posthog.__loaded) {
        return;
    }
    // The enterprise (tenant) name is a documented exception to the "no customer names in
    // analytics" rule — see the Enterprise cohort section of `docs/tracking-plan.md`. It is
    // sent only here, as a group property, never as an event property.
    posthog.group('enterprise', enterpriseId, {
        ...(enterprise?.enterpriseName != null && { name: enterprise.enterpriseName }),
    });
}

/**
 * Initializes the PostHog client once. Call only when the PostHogEnabled feature flag is true
 * and claims are available (caller must gate on truthy claims). Ingest host and API key come
 * from BFF ServiceDiscovery (PostHogSettings), never from VITE_* build vars. Claims are kept
 * current on every call — including after the client is loaded — and `before_send` reads the
 * latest value for `sanitizePostHogEvent` redaction.
 */
export function initializePostHog(config: InitializePostHogConfig): void {
    currentClaims = config.claims;

    const key = config.key?.trim();
    const host = config.host?.trim();

    if (!key) {
        return;
    }
    if (!host) {
        return;
    }
    if (posthog.__loaded) {
        registerEnterpriseGroup(config.enterprise);
        return;
    }

    posthog.init(key, {
        api_host: host,
        person_profiles: 'identified_only',
        enable_heatmaps: true,
        // Allowlist autocapture: nothing fires `$autocapture` unless the
        // element (or an ancestor) carries `data-ph-capture`. Engineers
        // must explicitly opt elements in — no element-text leaks by
        // default from random clicks.
        autocapture: {
            css_selector_allowlist: ['[data-ph-capture]'],
            capture_copied_text: false,
        },
        // If session replay is enabled, the cost of a single PII replay landing
        // in PostHog is high (stored, indexed, visible to anyone with
        // replay access), so EVERY masking key below is load-bearing.
        // Do NOT remove or relax any of them. Re-verify in a non-prod
        // environment after any PostHog SDK upgrade that every element
        // still renders as `***`.
        //
        // Layered defenses (each protects against the others failing):
        //  1. maskAllInputs       — every <input>/<textarea> value masked
        //  2. maskTextSelector: * — every element's text targeted for masking
        //  3. maskTextFn          — whatever rrweb captures, replace with '***'
        //                           (last-line defense if selector matching misses)
        //  4. blockSelector       — elements opted-out are NOT recorded at all;
        //                           tag sensitive panels with `data-ph-no-record`
        //  5. captureCanvas.recordCanvas: false — canvas can draw text; don't record it
        //  6. recordCrossOriginIframes / recordHeaders / recordBody — off
        //  7. enable_recording_console_log: false — devs log PII to console
        disable_session_recording: true /* always disabled until we can do the consent checks and FF */,
        enable_recording_console_log: false,
        session_recording: {
            maskAllInputs: true,
            maskTextSelector: '*',
            maskTextFn: () => '***',
            blockSelector: '[data-ph-no-record]',
            captureCanvas: { recordCanvas: false },
            recordCrossOriginIframes: false,
            recordHeaders: false,
            recordBody: false,
        },
        mask_all_text: true,
        mask_all_element_attributes: false,
        before_send: (event) => {
            return sanitizePostHogEvent(event, currentClaims);
        },
        loaded: (client) => {
            client.register({
                ...(config.claims?.environmentName != null && { environment: config.claims.environmentName }),
                ...(config.buildNumber != null && { build_number: config.buildNumber }),
            });
            registerEnterpriseGroup(config.enterprise);
        },
    });
}

export { posthog };
